import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCatalogLabels } from "@/i18n/catalog";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Heart, Lock, ShieldCheck, ChevronDown, ChevronUp, Clock, Sparkles, Star, BadgeCheck } from "lucide-react";
import { PillChip } from "@/components/toggles";
import STXThemeToggle from "@/components/stx/STXThemeToggle";
import BodyBlurredImage from "@/components/media/BodyBlurredImage";
import AVSBlurBadge from "@/components/media/AVSBlurBadge";
import StxFeed from "@/components/stx/StxFeed";

/** ISO-Alpha-2 → Flag-Emoji (regional indicator glyphs). */
function flagEmoji(code) {
  if (!code || code.length !== 2) return "";
  const cp = [...code.toUpperCase()].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65);
  return String.fromCodePoint(...cp);
}

export default function StillTouchedXPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const cat = useCatalogLabels();

  const STX_TABS = [
    { key: "shop", label: t("stx.tab_shop") },
    { key: "rescue", label: t("stx.tab_rescue") },
    { key: "damen", label: t("stx.tab_damen") },
    { key: "herren", label: t("stx.tab_herren") },
  ];
  const [gateState, setGateState] = useState("UNKNOWN"); // UNKNOWN, OPEN, PASSED, DENIED
  const [birthYear, setBirthYear] = useState("");
  const [birthMonth, setBirthMonth] = useState("");
  const [birthDay, setBirthDay] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [gateError, setGateError] = useState("");
  const [tab, setTab] = useState("shop");
  const [categories, setCategories] = useState([]);
  // iter172p · Damen/Herren-Shop-Layout: erst Geschlecht wählen, dann Kategorien filtern
  const [selectedGender, setSelectedGender] = useState(null);
  const [selectedCat, setSelectedCat] = useState("all");
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [sellers, setSellers] = useState([]);
  // iter88.5: STX-Katalog (14 Cats) + Sub-Kat-Filter + gender-basierte Listen
  const [stxCatalog, setStxCatalog] = useState(null); // { tree:[] }
  const [selectedSubCat, setSelectedSubCat] = useState("");
  // iter88.7: Seller-Sort (Trending / Alter)
  const [sellerSort, setSellerSort] = useState("trending");
  // iter172 · Listing-Sort + Altersbereich-Filter (Shop/Rescue-Views)
  const [listingSort, setListingSort] = useState("newest");
  const [ageRanges, setAgeRanges] = useState([]); // ["18-25","26-35",…]

  // Check stored gate state OR auto-pass for AVS-verified users
  useEffect(() => {
    const stored = sessionStorage.getItem("stx_gate");
    if (stored === "PASSED") {
      setGateState("PASSED");
      return;
    }
    // AVS-VERIFIED users skip the date-of-birth gate (sie haben Identitäts-/Alters-Check schon bestanden)
    if (user && user.age_verification_status === "VERIFIED") {
      setGateState("PASSED");
      sessionStorage.setItem("stx_gate", "PASSED");
    }
  }, [user]);

  const handleGateSubmit = async (e) => {
    e.preventDefault();
    setGateError("");
    try {
      const { data } = await api.post("/stilltouchedx/entry-gate", {
        birth_year: parseInt(birthYear),
        birth_month: parseInt(birthMonth),
        birth_day: parseInt(birthDay),
        confirmed_18_plus: confirmed,
      });
      if (data.result === "PASSED") {
        setGateState("PASSED");
        sessionStorage.setItem("stx_gate", "PASSED");
      } else if (data.result === "DENIED") {
        setGateState("DENIED");
        setGateError(data.message);
      } else {
        setGateError(data.message);
      }
    } catch (err) {
      setGateError(t("stx.gate_invalid"));
    }
  };

  const loadFeed = useCallback(async () => {
    if (gateState !== "PASSED") return;
    setLoading(true);
    try {
      // iter168 · News/FAQ raus aus Tabs — im MoreDropdown/Overlay geladen.
      // iter88.5: Damen/Herren-Tab zeigt Seller-Liste statt Feed
      if (tab === "damen" || tab === "herren") {
        const seg = tab === "damen" ? "FEMALE" : "MALE";
        const { data } = await api.get(`/sellers?area=STILLTOUCHEDX&segment=${seg}&sort=${sellerSort}`);
        setSellers(data.sellers || []);
        setTotal(data.total || 0);
        setLoading(false);
        return;
      }
      const section = tab === "rescue" ? "rescue" : "shop";
      // STX-Katalog + Feed parallel
      const ageParam = ageRanges.length > 0 ? `&age_range=${encodeURIComponent(ageRanges.join(","))}` : "";
      const [catData, feedRes] = await Promise.all([
        stxCatalog ? Promise.resolve({ tree: stxCatalog.tree }) : api.get("/stilltouchedx/catalog/categories").then((r) => r.data),
        api.get(`/stilltouchedx/feed?category=${selectedCat}&subcategory=${selectedSubCat || ""}&segment=all&section=${section}&sort=${listingSort}${ageParam}`),
      ]);
      if (!stxCatalog) setStxCatalog({ tree: catData.tree || [] });
      setCategories((catData.tree || []).map((n) => ({ code: n.category_code, label: n.label, gender: n.gender })));
      setListings(feedRes.data.listings || []);
      setTotal(feedRes.data.total || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [gateState, tab, selectedCat, selectedSubCat, sellerSort, stxCatalog, listingSort, ageRanges]);

  useEffect(() => { loadFeed(); }, [loadFeed]);

  const toggleWatch = async (listingId, e) => {
    e.stopPropagation();
    if (!user || user === false) { navigate("/login"); return; }
    try {
      const { data } = await api.post(`/listings/${listingId}/watch`);
      setListings(prev => prev.map(l => l.listing_id === listingId ? { ...l, is_watched: data.watched } : l));
    } catch { /* ignore */ }
  };

  // ─── Entry Gate Overlay ───────────────────────────────
  if (gateState !== "PASSED") {
    return (
      <div data-testid="stx-entry-gate" className="min-h-full bg-stx-bg text-stx-text flex flex-col">
        <div className="flex-1 flex flex-col items-center justify-center px-6 py-8 max-w-sm mx-auto">
          <div className="w-14 h-14 border border-stx-border flex items-center justify-center mb-6">
            <ShieldCheck size={28} className="text-stx-accent" />
          </div>
          <h1 className="text-xl font-bold tracking-tight text-center">{t("stx.gate_title")}</h1>
          <p className="text-sm text-stx-text-muted mt-2 text-center leading-relaxed">
            {t("stx.gate_desc")}
          </p>

          {gateState === "DENIED" && (
            <div className="mt-4 px-3 py-2 bg-red-900/30 border border-red-800 text-red-300 text-sm w-full">
              {gateError || t("stx.gate_denied")}
            </div>
          )}
          {gateError && gateState !== "DENIED" && (
            <div className="mt-4 px-3 py-2 bg-amber-900/30 border border-amber-800 text-amber-300 text-sm w-full">
              {gateError}
            </div>
          )}

          <form onSubmit={handleGateSubmit} className="mt-6 w-full flex flex-col gap-4">
            <div>
              <label className="text-[10px] font-medium text-stx-text-muted uppercase tracking-wider">{t("stx.gate_dob")}</label>
              <div className="flex gap-2 mt-1">
                <input
                  data-testid="stx-gate-day"
                  type="number"
                  placeholder={t("stx.gate_day")}
                  value={birthDay}
                  onChange={(e) => setBirthDay(e.target.value)}
                  className="flex-1 h-12 px-3 bg-stx-panel-2 border border-stx-border rounded-full text-stx-text text-sm text-center focus:outline-none focus:border-stx-border-strong"
                  min="1" max="31" required
                />
                <input
                  data-testid="stx-gate-month"
                  type="number"
                  placeholder={t("stx.gate_month")}
                  value={birthMonth}
                  onChange={(e) => setBirthMonth(e.target.value)}
                  className="flex-1 h-12 px-3 bg-stx-panel-2 border border-stx-border rounded-full text-stx-text text-sm text-center focus:outline-none focus:border-stx-border-strong"
                  min="1" max="12" required
                />
                <input
                  data-testid="stx-gate-year"
                  type="number"
                  placeholder={t("stx.gate_year")}
                  value={birthYear}
                  onChange={(e) => setBirthYear(e.target.value)}
                  className="flex-[1.5] h-12 px-3 bg-stx-panel-2 border border-stx-border rounded-full text-stx-text text-sm text-center focus:outline-none focus:border-stx-border-strong"
                  min="1900" max="2010" required
                />
              </div>
            </div>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                data-testid="stx-gate-confirm"
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                className="mt-0.5 accent-[color:var(--stx-accent)]"
              />
              <span className="text-xs text-stx-text-muted">
                {t("stx.gate_confirm_18")}
              </span>
            </label>
            <button
              data-testid="stx-gate-submit"
              type="submit"
              className="h-12 rounded-full bg-stx-accent text-stx-accent-fg text-sm font-medium transition-colors hover:bg-stx-accent-hover active:scale-[0.98]"
            >
              {t("stx.gate_submit")}
            </button>
          </form>

          <button
            data-testid="stx-gate-back"
            onClick={() => navigate("/")}
            className="mt-4 text-xs text-stx-text-muted hover:text-stx-text transition-colors"
          >
            {t("stx.gate_back")}
          </button>
        </div>
      </div>
    );
  }

  // ─── stilltouchedX Shop ───────────────────────────────
  return (
    <div data-testid="stilltouchedx-page" className="bg-stx-bg min-h-full text-stx-text">
      <div className={`w-full mx-auto ${tab === "feed" ? "max-w-2xl 2xl:max-w-full 2xl:mx-0" : "max-w-7xl 2xl:max-w-full"}`}>
      {/* STX Header */}
      <div className="px-4 pt-4 pb-2 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-lg font-bold tracking-tight">stilltouchedX</h1>
          <p className="text-xs text-stx-text-muted mt-0.5">{t("stx.offers_count", { count: total })}</p>
        </div>
        <STXThemeToggle />
      </div>

      {/* Top-Navigation (P4): Feed / Shop / Creator. Rescue + Damen/Herren als Sub-Chips. */}
      {(() => {
        const topGroup = tab === "feed" ? "feed" : (tab === "damen" || tab === "herren") ? "creator" : "shop";
        return (
          <>
            <div data-testid="stx-topnav" className="flex gap-1 px-4 py-2 overflow-x-auto scrollbar-hide md:grid md:grid-cols-3 md:gap-2 md:px-6 md:py-3 items-center">
              {[
                { k: "feed", label: t("stx.tab_feed", { defaultValue: "Feed" }), go: "feed" },
                { k: "shop", label: t("stx.tab_shop"), go: "shop" },
                { k: "creator", label: t("stx.tab_creator", { defaultValue: "Creator" }), go: "damen" },
              ].map((g) => (
                <PillChip key={g.k} testId={`stx-topnav-${g.k}`} active={topGroup === g.k}
                  onClick={() => { setTab(g.go); setSelectedCat("all"); }}
                  variant="stx" size="md" className="md:w-full md:justify-center">
                  {g.label}
                </PillChip>
              ))}
            </div>
            {topGroup === "shop" && (
              <div data-testid="stx-subnav-shop" className="flex gap-1 px-4 pb-1 md:px-6">
                <PillChip testId="stx-sub-shop" active={tab === "shop"} onClick={() => { setTab("shop"); setSelectedCat("all"); }} variant="stx" size="md">{t("stx.tab_shop")}</PillChip>
                <PillChip testId="stx-sub-rescue" active={tab === "rescue"} onClick={() => { setTab("rescue"); setSelectedCat("all"); }} variant="stx" size="md">{t("stx.tab_rescue")}</PillChip>
              </div>
            )}
            {topGroup === "creator" && (
              <div data-testid="stx-subnav-creator" className="flex gap-1 px-4 pb-1 md:px-6">
                <PillChip testId="stx-sub-damen" active={tab === "damen"} onClick={() => setTab("damen")} variant="stx" size="md">{t("stx.tab_damen")}</PillChip>
                <PillChip testId="stx-sub-herren" active={tab === "herren"} onClick={() => setTab("herren")} variant="stx" size="md">{t("stx.tab_herren")}</PillChip>
              </div>
            )}
          </>
        );
      })()}

      {tab === "feed" && <StxFeed />}

      {/* Category Chips — iter172p · Damen/Herren-Shop-Layout wie MP.
          Erst wählt der User „Damenartikel" oder „Herrenartikel", dann erscheinen
          die passenden Unter-Kategorien. */}
      {(tab === "shop" || tab === "rescue") && categories.length > 0 && (
        <>
          {selectedGender === null ? (
            <div data-testid="stx-gender-chooser" className="flex flex-wrap gap-2 px-4 py-3 border-b border-stx-border">
              <PillChip testId="stx-gender-women"
                onClick={() => { setSelectedGender("women"); setSelectedCat("all"); setSelectedSubCat(""); }}
                variant="stx" size="md">
                {t("stx.gender_women", { defaultValue: "Damenartikel" })}
              </PillChip>
              <PillChip testId="stx-gender-men"
                onClick={() => { setSelectedGender("men"); setSelectedCat("all"); setSelectedSubCat(""); }}
                variant="stx" size="md">
                {t("stx.gender_men", { defaultValue: "Herrenartikel" })}
              </PillChip>
            </div>
          ) : (
            <>
              <div data-testid="stx-cat-bar" className="flex flex-wrap gap-2 px-4 py-2 border-b border-stx-border">
                <PillChip testId="stx-gender-back"
                  onClick={() => { setSelectedGender(null); setSelectedCat("all"); setSelectedSubCat(""); }}
                  variant="stx" size="md">
                  ← {t("stx.back", { defaultValue: "Zurück" })}
                </PillChip>
                <PillChip testId="stx-cat-all"
                  active={selectedCat === "all"}
                  onClick={() => { setSelectedCat("all"); setSelectedSubCat(""); }}
                  variant="stx" size="md">
                  {t("stx.cat_all")}
                </PillChip>
                {categories.filter((c) => {
                  const g = (c.gender || "").toUpperCase();
                  return (selectedGender === "women" && (g === "F" || g === "WOMEN" || g === "FEMALE"))
                      || (selectedGender === "men"   && (g === "M" || g === "MEN"   || g === "MALE"));
                }).map((c) => (
                  <PillChip
                    key={c.code}
                    testId={`stx-cat-${c.code}`}
                    active={selectedCat === c.code}
                    onClick={() => { setSelectedCat(c.code); setSelectedSubCat(""); }}
                    variant="stx"
                    size="md"
                  >
                    {cat.stxCat(c.code, c.label)}
                  </PillChip>
                ))}
              </div>
              {stxCatalog && selectedCat !== "all" && (
                <div data-testid="stx-subcat-bar" className="flex flex-wrap gap-1.5 px-4 py-2 border-b border-stx-border">
                  {(stxCatalog.tree.find((n) => n.category_code === selectedCat)?.subcategories || []).map((s) => (
                    <PillChip key={s.code} testId={`stx-subcat-${s.code}`}
                      active={selectedSubCat === s.code}
                      onClick={() => setSelectedSubCat(selectedSubCat === s.code ? "" : s.code)}
                      variant="stx" size="sm">
                      {cat.stxSub(selectedCat, s.code, s.label)}
                    </PillChip>
                  ))}
                </div>
              )}
            </>
          )}

          {/* iter172 · Desktop-Inline-Suchfeld (unter Kategorien) */}
          <div data-testid="stx-desktop-search" className="hidden md:block px-4 pt-3">
            <div className="relative max-w-lg">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input
                data-testid="stx-desktop-search-input"
                type="search"
                placeholder={t("stx.search_ph", { defaultValue: "Artikel in stilltouchedX suchen…" })}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && e.currentTarget.value.trim()) {
                    navigate(`/search?scope=STILLTOUCHEDX&q=${encodeURIComponent(e.currentTarget.value.trim())}`);
                  }
                }}
                className="w-full h-10 pl-9 pr-3 rounded-full bg-stx-panel border border-stx-border text-sm text-stx-text placeholder:text-stx-text-muted focus:outline-none focus:ring-1 focus:ring-stx-accent"
              />
            </div>
          </div>

          {/* iter172 · Sort + Altersbereich-Filter (Shop/Rescue) */}
          <div data-testid="stx-shop-controls" className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-stx-border">
            <span className="text-[10px] uppercase tracking-wider text-stx-text-muted">Sortierung:</span>
            {[
              { v: "newest", l: "Neueste zuerst" },
              { v: "price_asc", l: "Preis ↑" },
              { v: "price_desc", l: "Preis ↓" },
            ].map((o) => (
              <PillChip key={o.v} testId={`stx-listing-sort-${o.v}`}
                active={listingSort === o.v}
                onClick={() => setListingSort(o.v)}
                variant="stx" size="sm">
                {o.l}
              </PillChip>
            ))}
            <span className="ml-2 text-[10px] uppercase tracking-wider text-stx-text-muted">Alter:</span>
            {["18-25","26-35","36-45","46-55","56+"].map((r) => (
              <PillChip key={r} testId={`stx-age-${r}`}
                active={ageRanges.includes(r)}
                onClick={() => setAgeRanges(ageRanges.includes(r) ? ageRanges.filter(x => x !== r) : [...ageRanges, r])}
                variant="stx" size="sm">
                {r}
              </PillChip>
            ))}
            {ageRanges.length > 0 && (
              <button
                data-testid="stx-age-clear"
                onClick={() => setAgeRanges([])}
                className="text-[10px] text-stx-text-muted underline"
              >
                Zurücksetzen
              </button>
            )}
          </div>
        </>
      )}

      {/* iter88.15d: Damen/Herren-Seller-Grid — Valeszka-Style */}
      {(tab === "damen" || tab === "herren") && (
        loading ? (
          <div className="flex justify-center py-16">
            <div className="w-6 h-6 border-2 border-stx-border border-t-stx-accent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Sort-Bar — Pill-Chips mit Salmon-Aktiv */}
            <div data-testid="stx-seller-sort" className="flex flex-wrap gap-2 px-4 py-3 border-b border-stx-border">
              {[
                { v: "trending", l: t("stx.seller_sort_trending") },
                { v: "newest", l: t("stx.seller_sort_newest") },
                { v: "oldest", l: t("stx.seller_sort_oldest") },
                { v: "listings_desc", l: t("stx.seller_sort_listings_desc") },
              ].map((o) => (
                <PillChip key={o.v} testId={`stx-seller-sort-${o.v}`}
                  active={sellerSort === o.v}
                  onClick={() => setSellerSort(o.v)}
                  variant="stx" size="md">
                  {o.l}
                </PillChip>
              ))}
            </div>
            {sellers.length === 0 ? (
              <div className="px-4 py-16 text-center">
                <div className="w-14 h-14 rounded-full bg-stx-accent-soft mx-auto flex items-center justify-center mb-3">
                  <Sparkles size={20} className="text-stx-accent" />
                </div>
                <p className="text-sm text-stx-text-muted">
                  {tab === "damen" ? t("stx.no_female_sellers") : t("stx.no_male_sellers")}
                </p>
              </div>
            ) : (
              <div data-testid={`stx-sellers-${tab}`} className="grid gap-3 px-4 py-4" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))" }}>
                {sellers.map((s) => {
                  const bioImg = s.bio_image_url || s.avatar_url || "";
                  const flag = flagEmoji(s.country_code || "");
                  const stars = Math.round(Number(s.avg_rating) || 0);
                  const reviews = Number(s.review_count) || 0;
                  return (
                    <button
                      key={s.seller_id}
                      data-testid={`stx-seller-${s.handle || s.seller_id}`}
                      onClick={() => s.handle && navigate(`/user/${s.handle}/stx`)}
                      disabled={!s.handle}
                      className="group rounded-xl overflow-hidden bg-stx-panel border border-stx-border hover:border-stx-accent transition-all text-left disabled:opacity-40"
                    >
                      {/* Bio-Bild — nicht Artikel */}
                      <div className="relative aspect-[3/4] overflow-hidden bg-stx-panel-2">
                        {bioImg ? (
                          <img src={bioImg} alt=""
                            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                            loading="lazy" />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-stx-panel-2 via-stx-bg to-stx-panel flex items-center justify-center">
                            <span className="text-3xl font-bold text-stx-text-dim">
                              {(s.display_name || "?")[0]?.toUpperCase()}
                            </span>
                          </div>
                        )}
                        {typeof s.listing_count === "number" && s.listing_count > 0 && (
                          <span
                            data-testid={`stx-seller-listing-count-${s.handle || s.seller_id}`}
                            className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-stx-bg/70 backdrop-blur text-stx-accent text-[10px] font-bold tabular-nums">
                            {t("stx.articles_count", { count: s.listing_count })}
                          </span>
                        )}
                      </div>
                      {/* Name-Zeile: Display-Name + Verified + Flagge */}
                      <div className="px-3 pt-2.5 pb-1 flex items-center gap-1.5 min-w-0">
                        <p className="text-sm font-bold text-stx-text truncate leading-tight">
                          {s.display_name || "—"}
                        </p>
                        {s.is_verified && (
                          <BadgeCheck size={13} className="text-stx-accent shrink-0" data-testid={`stx-seller-verified-${s.handle || s.seller_id}`} />
                        )}
                        {flag && (
                          <span className="text-sm leading-none shrink-0" title={s.country_code} data-testid={`stx-seller-country-${s.handle || s.seller_id}`}>{flag}</span>
                        )}
                      </div>
                      {/* Rating-Zeile: 5 Sterne + (N) — oder „Neu" */}
                      <div className="px-3 pb-3 flex items-center gap-1" data-testid={`stx-seller-rating-${s.handle || s.seller_id}`}>
                        {reviews > 0 ? (
                          <>
                            <div className="flex gap-0.5">
                              {[1, 2, 3, 4, 5].map((i) => (
                                <Star key={i} size={11}
                                  className={i <= stars ? "text-stx-accent fill-stx-accent" : "text-stx-border-strong"} />
                              ))}
                            </div>
                            <span className="text-[10px] text-stx-text-muted tabular-nums">({reviews})</span>
                          </>
                        ) : (
                          <span className="text-[10px] uppercase tracking-wider font-semibold text-stx-text-dim">{t("stx.seller_new")}</span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )
      )}

      {/* Listing Grid — nur wenn NICHT Damen/Herren-Tab */}
      {(tab === "shop" || tab === "rescue") && (loading ? (
        <div className="flex justify-center py-16">
          <div className="w-6 h-6 border-2 border-stx-border border-t-stx-accent rounded-full animate-spin" />
        </div>
      ) : listings.length === 0 ? (
        <div className="px-4 py-16 text-center text-sm text-stx-text-muted">
          {t("stx.no_listings")}
        </div>
      ) : (
        <div data-testid="stx-listing-grid" className="grid gap-3 p-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))" }}>
          {listings.map((listing) => {
            // Iter77b (Owner-Vorgabe 2026-07-10): intelligenter AVS-Blur analog Detail-Seite.
            // Titelbild aus title_photo_url oder media_urls[0]. Wenn Titelbild ein
            // Trage-Foto ist → nur AVS-verifizierte sehen scharf. Reine Artikel-Fotos
            // bleiben IMMER klar sichtbar.
            const isAvsVerified = user?.age_verification_status === "VERIFIED";
            const wornSet = new Set([...(listing.worn_photo_urls || []), ...(listing.body_content_urls || [])]);
            // iter173d · Fallback auf Trage-Foto, falls kein neutrales Artikelbild als Titel
            // existiert. Sonst zeigte die Kachel eine weiße Box + Schloss statt eines
            // geblurrten Bildes mit 18+-Marker (analog Detail-Seite).
            const titleUrl = listing.title_photo_url
              || (listing.media_urls || [])[0]
              || (listing.worn_photo_urls || [])[0]
              || (listing.body_content_urls || [])[0];
            const titleIsWorn = titleUrl && wornSet.has(titleUrl);
            // iter172o · body_regions_by_url[titleUrl] entscheidet, ob wir das
            // serverseitig geblurrte PNG holen (Admin-Testtool-Logik) oder das
            // rohe Bild mit CSS-Full-Blur maskieren.
            const regionsForTitle = (listing.body_regions_by_url || {})[titleUrl] || [];
            const shouldBlur = titleIsWorn && !isAvsVerified;
            const hasKnownRegions = shouldBlur && regionsForTitle.length > 0;
            const fullBlurClass = shouldBlur && !hasKnownRegions ? "blur-2xl scale-110" : "";
            const isMystery = listing.listing_section === "RESCUE_MYSTERY";
            const mysterySoldOut = isMystery && listing.mystery_available === false;
            const onCardClick = isMystery
              ? () => navigate(`/stilltouchedx/mystery/${listing.mystery_category}`)
              : () => navigate(`/stilltouchedx/${listing.listing_id}`);
            const displayUrl = titleUrl;
            const totalImages = (listing.media_urls?.length || 0) + (listing.worn_photo_urls?.length || 0);
            return (
            <div
              key={listing.listing_id}
              data-testid={`stx-listing-${listing.listing_id}`}
              onClick={onCardClick}
              className="bg-stx-panel flex flex-col text-left group cursor-pointer rounded-2xl overflow-hidden border border-stx-border hover:border-stx-accent transition-colors"
            >
              {/* Image / Blur placeholder */}
              <div className="relative aspect-[3/4] bg-stx-panel-2 overflow-hidden">
                {displayUrl ? (
                  hasKnownRegions ? (
                    <BodyBlurredImage
                      listingId={listing.listing_id}
                      rawUrl={displayUrl}
                      hasRegions={true}
                      isViewerAvsVerified={isAvsVerified}
                      className="w-full h-full object-cover bg-stx-bg"
                      testId={`stx-feed-image-${listing.listing_id}`}
                    />
                  ) : (
                    <img src={displayUrl} alt="" className={`w-full h-full object-cover bg-stx-bg ${fullBlurClass}`} loading="lazy" />
                  )
                ) : isMystery ? (
                  <div className="w-full h-full bg-gradient-to-br from-stx-panel-2 via-stx-bg to-stx-panel-2 flex flex-col items-center justify-center gap-1">
                    <Sparkles size={28} className="text-amber-300" />
                    <span className="text-[10px] text-stx-accent tracking-wider">{t("stx.mystery")}</span>
                  </div>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Lock size={24} className="text-stx-border-strong" />
                  </div>
                )}
                {isMystery && (
                  <span
                    data-testid={`stx-mystery-badge-${listing.listing_id}`}
                    className="absolute top-2 left-2 z-10 px-1.5 py-0.5 bg-amber-500/90 text-black text-[9px] font-bold tracking-wider">
                    {t("stx.mystery")}
                  </span>
                )}
                {mysterySoldOut && (
                  <div data-testid={`stx-mystery-soldout-${listing.mystery_category}`} className="absolute inset-0 bg-black/70 flex items-center justify-center">
                    <span className="px-3 py-1.5 bg-stx-accent text-stx-accent-fg text-[10px] font-bold tracking-wider">{t("stx.mystery_soldout")}</span>
                  </div>
                )}
                {!isMystery && totalImages > 1 && (
                  <span data-testid={`stx-img-count-${listing.listing_id}`}
                    className="absolute top-2 left-2 z-10 px-2 py-0.5 rounded-full bg-black/70 backdrop-blur-sm text-white text-[10px] font-bold">
                    +{totalImages - 1}
                  </span>
                )}
                {shouldBlur && !isMystery && !hasKnownRegions && (
                  <AVSBlurBadge size="md" testId={`stx-avs-badge-${listing.listing_id}`} />
                )}
                {!isMystery && (
                  <button
                    data-testid={`stx-watch-${listing.listing_id}`}
                    onClick={(e) => toggleWatch(listing.listing_id, e)}
                    className="absolute top-2 right-2 p-1 bg-stx-bg/60 backdrop-blur-sm transition-colors"
                  >
                    <Heart size={14} className={listing.is_watched ? "fill-stx-accent text-stx-accent" : "text-stx-text-muted"} />
                  </button>
                )}
              </div>
              {/* Info */}
              <div className="p-2">
                <p className="text-xs font-medium text-stx-text line-clamp-2 min-h-[2rem]">{listing.title}</p>
                {!isMystery && listing.rescue_phase === "RESCUE" && (
                  <div
                    data-testid={`stx-rescue-phase-${listing.listing_id}`}
                    className={`mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-medium rounded ${
                      (listing.rescue_phase_ends_in_days ?? 99) <= 3
                        ? "bg-red-500/15 text-red-300 border border-red-700/40"
                        : "bg-green-500/15 text-green-300 border border-green-700/40"
                    }`}
                    title={(listing.rescue_phase_ends_in_days ?? 99) <= 3 ? t("stx.rescue_last_chance_title") : t("stx.rescue_price_reduced_title")}
                  >
                    <Clock size={9} />
                    {(listing.rescue_phase_ends_in_days ?? 99) <= 3 ? t("stx.rescue_last_chance") : t("stx.rescue_price_reduced")}
                  </div>
                )}
                <div className="flex items-center justify-between mt-0.5">
                  {isMystery ? (
                    <p className="text-sm font-bold text-stx-text">
                      {mysterySoldOut ? <span className="text-stx-text-muted text-xs">{t("stx.mystery_empty")}</span> : <>
                        {Number(listing.price || 0).toFixed(2)} {listing.currency || "EUR"}
                        <span className="text-[9px] text-stx-text-muted ml-1">({t("stx.mystery_items_short", { count: listing.mystery_item_count })})</span>
                      </>}
                    </p>
                  ) : (
                    <p className="text-sm font-bold text-stx-text">{listing.price?.toFixed(2)} {listing.currency}</p>
                  )}
                  {/* Bearbeitungszeit-Badge entfernt aus STX-Übersicht (iter110) —
                      Info wird nur noch in der Artikeldetailseite gezeigt. */}
                </div>
              </div>
            </div>
            );
          })}
        </div>
      ))}

      {/* iter168 · News + FAQ nun via MoreDropdown/Overlay (nicht mehr im Tab-Body). */}
      </div>
    </div>
  );
}
